import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, Mail, Sparkles } from "lucide-react";
import { socialLinks, bookMeetingUrl } from "@/data/social";
import { useActiveBanner } from "@/hooks/useActiveBanner";
import { useNextEvent } from "@/hooks/useNextEvent";
import { cn } from "@/lib/utils";

interface Props {
  compact?: boolean;
  onNavigate?: () => void;
}

const MegaUtilityBar = ({ compact = false, onNavigate }: Props) => {
  const { data: banner } = useActiveBanner();
  const { data: nextEvent } = useNextEvent({ upcomingOnly: true });

  // Singleton override: hidden → no promo; override → custom; auto → event.
  const promo =
    banner?.mode === "hidden"
      ? null
      : banner?.mode === "override"
        ? banner.text?.trim()
          ? { href: banner.cta_url ?? "#", text: banner.text, cta_label: banner.cta_label }
          : null
        : nextEvent
          ? {
              href: nextEvent.href,
              text: `Meet us at ${nextEvent.title} — ${[nextEvent.isVirtual ? "Virtual" : nextEvent.location, nextEvent.dateLabel].filter(Boolean).join(" · ")}`,
              cta_label: "View event",
            }
          : null;


  return (
    <div
      className={cn(
        "relative border-t border-slate-200 bg-slate-50",
        compact ? "px-5 py-3" : "px-5 py-3",
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px opacity-50"
        style={{ background: "var(--gradient-primary-line)" }}
      />
      <div className={cn("flex items-center gap-4 flex-wrap", compact && "gap-3")}>
        <Link
          to={bookMeetingUrl}
          onClick={onNavigate}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-900 hover:text-primary transition-colors"
        >
          <CalendarDays className="h-3.5 w-3.5" />
          Book a meeting
        </Link>
        <a
          href="mailto:contact@deepc.ai"
          onClick={onNavigate}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-700 hover:text-slate-900 transition-colors"
        >
          <Mail className="h-3.5 w-3.5" />
          Contact
        </a>
        <span aria-hidden className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          {socialLinks.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noreferrer"
              aria-label={s.label}
              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition-colors"
            >
              <s.icon className="h-3.5 w-3.5" />
            </a>
          ))}
        </div>

        {promo && (
          <Link
            to={promo.href}
            onClick={onNavigate}
            className="ml-auto group inline-flex min-w-0 max-w-full items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-[11px] font-medium text-slate-900 hover:bg-primary/15 transition-colors"
          >
            <Sparkles className="h-3 w-3 shrink-0 text-primary" />
            <span className="uppercase tracking-mono text-primary whitespace-nowrap shrink-0">Event</span>
            <span className="text-slate-700 truncate min-w-0 max-w-[18ch] sm:max-w-[36ch] lg:max-w-[48ch]">{promo.text}</span>
            {promo.cta_label && (
              <span className="inline-flex items-center gap-0.5 text-primary whitespace-nowrap shrink-0">
                {promo.cta_label}
                <ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            )}
          </Link>
        )}
      </div>
    </div>
  );
};

export default MegaUtilityBar;
